// AutopilotPanel — slots into the CompliancePage drawer.
// Shows "Draft with AI" button when the obligation's catalog_code maps to a
// registered template, plus draft-history list. Two-pane review modal renders
// the AI output as an actual form (markdown → HTML) with a Print button.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

const SUPPORTED_CODES: Record<string, string> = {
  'FED-LAB-WH347': 'WH-347 Davis-Bacon weekly payroll',
  'FED-ENV-USACE': 'USACE §404 Pre-Construction Notification',
  'STA-Q-PASS':    'BEAD Challenge / Rebuttal',
};

type Obligation = { id: string; code: string; status: string };

type Draft = {
  id: string;
  obligation_id: string;
  catalog_code: string;
  template_version: string;
  model: string;
  source_data: any;
  drafted_text: string;
  user_action: string | null;
  user_action_at: string | null;
  user_action_notes: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  created_at: string;
};

export default function AutopilotPanel({ obligation }: { obligation: Obligation }) {
  const qc = useQueryClient();
  const [reviewDraftId, setReviewDraftId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const supported = SUPPORTED_CODES[obligation.code];
  const draftableStatus = ['pending', 'in_progress'].includes(obligation.status);

  const { data: status } = useQuery<any>({
    queryKey: ['autopilot-status'],
    queryFn: () => api.get('/autopilot/status'),
    staleTime: 60_000,
  });
  const autopilotEnabled = status?.enabled === true;

  const { data: history } = useQuery<{ drafts: Draft[] }>({
    queryKey: ['autopilot-drafts', obligation.id],
    queryFn: () => api.get('/autopilot/obligations/' + obligation.id + '/drafts'),
  });

  const draftMut = useMutation({
    mutationFn: () => api.post('/autopilot/obligations/' + obligation.id + '/draft', {}),
    onSuccess: (res: any) => {
      setError(null);
      setReviewDraftId(res.draft_id);
      qc.invalidateQueries({ queryKey: ['autopilot-drafts', obligation.id] });
    },
    onError: (e: any) => setError(e?.message ?? 'Draft failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (draftIdToDel: string) => api.del('/autopilot/drafts/' + draftIdToDel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['autopilot-drafts', obligation.id] });
    },
    onError: (e: any) => setError('Delete failed: ' + (e?.message ?? '')),
  });

  if (!supported) return null;

  return (
    <div style={{
      marginTop: 16, padding: 12, background: '#faf5ff',
      border: '1px solid #d8b4fe', borderRadius: 6,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '.05em' }}>
            ✦ Compliance Autopilot
          </div>
          <div style={{ fontSize: 11, color: '#581c87', marginTop: 2 }}>
            Phase 1 data-gathering template: <strong>{supported}</strong>
          </div>
          {obligation.code === "FED-LAB-WH347" ? (
            <div style={{ fontSize: 10, color: "#16a34a", marginTop: 1, fontStyle: "italic" }}>
              Phase 2A: approving generates the official Form WH-347 (OMB 1235-0008) + Form WH-348 PDF.
            </div>
          ) : (
            <div style={{ fontSize: 10, color: "#a78bfa", marginTop: 1, fontStyle: "italic" }}>
              Phase 1 (data only). Phase 2 template still being built for this obligation type.
            </div>
          )}
        </div>
      </div>

      {!autopilotEnabled && (
        <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', padding: '4px 8px', borderRadius: 3, marginTop: 6 }}>
          Autopilot disabled in plant settings or Anthropic key missing.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          disabled={!autopilotEnabled || !draftableStatus || draftMut.isPending}
          onClick={() => draftMut.mutate()}
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600,
            background: (autopilotEnabled && draftableStatus) ? '#7c3aed' : '#e9d5ff',
            color: '#fff', border: 0, borderRadius: 4,
            cursor: (autopilotEnabled && draftableStatus) ? 'pointer' : 'not-allowed',
          }}
          title={!draftableStatus ? "Status is '" + obligation.status + "' — drafts only available for pending/in_progress" : undefined}
        >
          {draftMut.isPending ? 'Drafting…' : '✦ Draft with AI'}
        </button>
        {!draftableStatus && (
          <span style={{ fontSize: 10, color: '#94a3b8' }}>
            (only pending / in_progress obligations can be drafted)
          </span>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 8, padding: '4px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 3, fontSize: 11 }}>
          Error: {error}
        </div>
      )}

      {history && history.drafts.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>
            Draft history ({history.drafts.length})
          </div>
          <div style={{ background: '#fff', border: '1px solid #e9d5ff', borderRadius: 4 }}>
            {history.drafts.map((d) => (
              <div key={d.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 10px', borderBottom: '1px solid #f3e8ff', fontSize: 11,
              }}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {d.template_version}
                    <span style={{ color: '#94a3b8', fontSize: 10, marginLeft: 6, fontWeight: 400 }}>
                      {new Date(d.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: 10 }}>
                    model {d.model}
                    {d.prompt_tokens && ' · ' + (d.prompt_tokens + (d.completion_tokens ?? 0)) + ' tokens'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  {d.user_action ? (
                    <>
                      <span style={{
                        padding: "2px 8px", borderRadius: 3, fontSize: 9, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: ".04em",
                        background: d.user_action === "approved" ? "#dcfce7" : "#fee2e2",
                        color: d.user_action === "approved" ? "#065f46" : "#991b1b",
                      }}>{d.user_action}</span>
                      {(d as any).drafted_pdf_id && (
                        <a
                          href={"/api/bead/documents/" + (d as any).drafted_pdf_id}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            padding: "3px 10px", fontSize: 10, fontWeight: 700,
                            background: "#0f766e", color: "#fff", borderRadius: 3,
                            textDecoration: "none", marginLeft: 4,
                          }}
                        >Open PDF</a>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => setReviewDraftId(d.id)}
                      style={{
                        padding: "3px 10px", fontSize: 10, fontWeight: 600,
                        background: "#7c3aed", color: "#fff", border: 0, borderRadius: 3, cursor: "pointer",
                      }}
                    >Review</button>
                  )}
                  <button
                    onClick={() => {
                      const label = d.user_action === 'approved'
                        ? 'This will delete the AI draft AND its generated PDF/attachment. Continue?'
                        : 'Delete this draft?';
                      if (window.confirm(label)) deleteMut.mutate(d.id);
                    }}
                    disabled={deleteMut.isPending}
                    title="Delete this draft"
                    style={{
                      marginLeft: 4, padding: "3px 8px", fontSize: 10,
                      background: "transparent", color: "#94a3b8",
                      border: "1px solid #e2e8f0", borderRadius: 3, cursor: "pointer",
                    }}
                  >🗑</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {reviewDraftId && (
        <DraftReviewModal
          draftId={reviewDraftId}
          obligationId={obligation.id}
          onClose={() => setReviewDraftId(null)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Two-pane review modal
// ===========================================================================
function DraftReviewModal({
  draftId, obligationId, onClose,
}: {
  draftId: string;
  obligationId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');

  const { data: draft, isLoading } = useQuery<Draft>({
    queryKey: ['autopilot-draft', draftId],
    queryFn: () => api.get('/autopilot/drafts/' + draftId),
  });

  const approveMut = useMutation({
    mutationFn: () => api.post("/autopilot/drafts/" + draftId + "/approve", { notes: notes || undefined }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ["autopilot-drafts", obligationId] });
      qc.invalidateQueries({ queryKey: ["obligations"] });
      qc.invalidateQueries({ queryKey: ["compliance-summary"] });
      if (res?.pdf_render_error) {
        alert("Approval saved, but PDF render failed: " + res.pdf_render_error + ". Draft is marked approved without an attached form. Check backend logs.");
        return;
      }
      if (res?.pdf_document_id) {
        window.open("/api/bead/documents/" + res.pdf_document_id, "_blank");
      }
      onClose();
    },
  });
  const rejectMut = useMutation({
    mutationFn: () => api.post('/autopilot/drafts/' + draftId + '/reject', { notes: notes || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['autopilot-drafts', obligationId] });
      onClose();
    },
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '92vw', maxWidth: 1300, height: '90vh',
          background: '#fff', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '12px 18px', borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '.06em' }}>
              {draft?.catalog_code === "FED-LAB-WH347"
                ? "✦ Generate official Form WH-347"
                : "✦ AI-drafted compliance filing"}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
              {draft?.catalog_code}
              {draft && (
                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8, fontWeight: 400 }}>
                  {draft.template_version} · {draft.model} · {new Date(draft.created_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 420px', minHeight: 0 }}>
          {/* Left: form-style rendered output */}
          <div style={{ padding: 24, overflowY: 'auto', borderRight: '1px solid #e2e8f0', background: '#f8fafc' }}>
            {draft?.catalog_code === "FED-LAB-WH347" ? (
              <div style={{
                background: "#dcfce7", border: "2px solid #16a34a", borderRadius: 4,
                padding: "10px 14px", marginBottom: 14,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#14532d", textTransform: "uppercase", letterSpacing: ".06em" }}>
                  Submittable form ready (Phase 2A)
                </div>
                <div style={{ fontSize: 12, color: "#166534", marginTop: 4, lineHeight: 1.5 }}>
                  Approving this draft generates the <strong>official Form WH-347</strong> (Davis-Bacon weekly
                  certified payroll, OMB 1235-0008) + <strong>Form WH-348</strong> (Statement of Compliance per
                  29 CFR 5.5) as a 2-page PDF, attached to this obligation. The output below is the AI's
                  anomaly-review notes that go into the WH-348 Remarks section. The actual form is
                  rendered mechanically from the source data — review citations on the right.
                </div>
              </div>
            ) : (
              <div style={{
                background: "#fef3c7", border: "2px solid #f59e0b", borderRadius: 4,
                padding: "10px 14px", marginBottom: 14,
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: ".06em" }}>
                  Phase 1 - Content Draft (Not Submittable As-Is)
                </div>
                <div style={{ fontSize: 12, color: "#78350f", marginTop: 4, lineHeight: 1.5 }}>
                  This output contains the correct underlying data + citations, but is <strong>NOT</strong> in
                  the format the receiving agency requires.
                  <br />- <strong>§404 PCN</strong> (Phase 2B): USACE Omaha District PCN Submittal Checklist + ENG Form 4345 - coming next
                  <br />- <strong>BEAD Challenge</strong> (Phase 2C): state-portal JSON payload (NTIA Challenge & Rebuttal API schema) - coming after
                  <br /><br />
                  Until those land, treat this as a <strong>data-gathering draft</strong> only. Do not submit.
                </div>
              </div>
            )}
            {isLoading ? <div>Loading…</div> :
              (draft?.drafted_text && draft.drafted_text.trim().length > 0) ? (
                <div style={{
                  background: '#fff', padding: '32px 40px',
                  border: '1px solid #cbd5e1', borderRadius: 4,
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  color: '#0f172a', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                  <MarkdownView text={draft.drafted_text} />
                </div>
              ) : (
                <div style={{
                  background: '#fff', padding: '32px 40px',
                  border: '1px dashed #cbd5e1', borderRadius: 4,
                  color: '#475569', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 36, color: '#cbd5e1', marginBottom: 8 }}>📄</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                    {draft?.catalog_code === "FED-LAB-WH347"
                      ? "Form rendered mechanically from source data"
                      : "No AI text generated"}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', maxWidth: 480, margin: '0 auto', lineHeight: 1.5 }}>
                    {draft?.catalog_code === "FED-LAB-WH347" ? (
                      <>The official Form WH-347 + WH-348 PDF will be built from the
                        <strong> source data on the right</strong> when you click <strong>Generate</strong>.
                        AI anomaly notes are optional supplementary material — none for this draft.</>
                    ) : (
                      <>Source data was gathered (see right panel). Click Approve to save as a data-only draft.</>
                    )}
                  </div>
                </div>
              )
            }
          </div>

          {/* Right: source data */}
          <div style={{ padding: 18, overflowY: 'auto', background: '#fff' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
              Source data (what the AI saw)
            </div>
            {draft?.source_data ? (
              <SourceDataView source={draft.source_data} />
            ) : <div style={{ fontSize: 11, color: '#94a3b8' }}>No source data captured.</div>}
          </div>
        </div>

        <div style={{
          padding: '12px 18px', borderTop: '1px solid #e2e8f0',
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          background: '#f8fafc',
        }}>
          {draft?.drafted_text && draft.drafted_text.trim().length > 0 && (
            <button
              onClick={() => printDraft(draft.drafted_text, (draft.catalog_code ?? 'Compliance Filing') + ' - ' + (draft.template_version ?? ''))}
              style={{
                padding: '7px 12px', fontSize: 12, fontWeight: 600,
                background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer',
              }}
            >🖨 Print AI notes</button>
          )}
          <input
            type="text"
            placeholder="Optional notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            style={{ flex: 1, minWidth: 180, padding: '6px 10px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3 }}
          />
          <button
            onClick={() => rejectMut.mutate()}
            disabled={rejectMut.isPending}
            style={{
              padding: '7px 14px', fontSize: 12, fontWeight: 600,
              background: '#fff', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer',
            }}
          >{rejectMut.isPending ? 'Rejecting…' : 'Reject & edit manually'}</button>
          <button
            onClick={() => approveMut.mutate()}
            disabled={approveMut.isPending}
            style={{
              padding: '7px 16px', fontSize: 12, fontWeight: 700,
              background: '#7c3aed', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer',
            }}
          >{approveMut.isPending
              ? (draft?.catalog_code === "FED-LAB-WH347" ? "Generating PDF..." : "Saving...")
              : (draft?.catalog_code === "FED-LAB-WH347"
                  ? "Generate official Form WH-347 PDF"
                  : "Save data draft (Phase 1 - not submittable)")}</button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// MarkdownView — minimal renderer for the AI output (form-friendly)
// ===========================================================================
type Block =
  | { kind: 'h'; level: 1 | 2 | 3; text: string }
  | { kind: 'hr' }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'table'; rows: string[][]; hasHeader: boolean };

function parseBlocks(input: string): Block[] {
  const lines = input.replace(/\r\n/g, '\n').split('\n');
  const out: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const trimmed = (lines[i] ?? '').trim();
    if (!trimmed) { i++; continue; }
    if (/^---+$/.test(trimmed)) { out.push({ kind: 'hr' }); i++; continue; }
    const h = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (h) {
      out.push({ kind: 'h', level: h[1].length as 1 | 2 | 3, text: h[2].trim() });
      i++; continue;
    }
    if (trimmed.startsWith('|')) {
      const rows: string[][] = [];
      let hasHeader = false;
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        const r = (lines[i] ?? '').trim();
        if (/^\|[-:\s|]+\|$/.test(r)) { hasHeader = true; i++; continue; }
        const cells = r.split('|').slice(1, -1).map((c) => c.trim());
        rows.push(cells);
        i++;
      }
      out.push({ kind: 'table', rows, hasHeader });
      continue;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^[-*]\s+/, ''));
        i++;
      }
      out.push({ kind: 'ul', items });
      continue;
    }
    const para: string[] = [trimmed];
    i++;
    while (i < lines.length) {
      const t = (lines[i] ?? '').trim();
      if (!t) break;
      if (/^(#{1,3}\s|---+$|\||[-*]\s)/.test(t)) break;
      para.push(t); i++;
    }
    out.push({ kind: 'p', text: para.join(' ') });
  }
  return out;
}

function inlineParts(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0, key = 0;
  let buf = '';
  const flush = () => { if (buf) { out.push(buf); buf = ''; } };
  while (i < text.length) {
    if (text.slice(i, i + 2) === '**') {
      const end = text.indexOf('**', i + 2);
      if (end > i + 2) {
        flush();
        out.push(<strong key={key++}>{text.slice(i + 2, end)}</strong>);
        i = end + 2; continue;
      }
    }
    if (text[i] === '*') {
      const end = text.indexOf('*', i + 1);
      if (end > i + 1) {
        flush();
        out.push(<em key={key++}>{text.slice(i + 1, end)}</em>);
        i = end + 1; continue;
      }
    }
    buf += text[i++];
  }
  flush();
  return out;
}

function MarkdownView({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  return (
    <div style={{ fontSize: 13, lineHeight: 1.55 }}>
      {blocks.map((b, i) => {
        if (b.kind === 'hr') return <hr key={i} style={{ border: 0, borderTop: '1px solid #cbd5e1', margin: '18px 0' }} />;
        if (b.kind === 'h') {
          const sizes: Record<number, number> = { 1: 22, 2: 17, 3: 14 };
          const margins: Record<number, string> = { 1: '24px 0 12px', 2: '18px 0 8px', 3: '14px 0 6px' };
          return (
            <div key={i} style={{
              fontSize: sizes[b.level], fontWeight: 700, color: '#0f172a',
              margin: margins[b.level],
              borderBottom: b.level <= 2 ? '1px solid #cbd5e1' : 'none',
              paddingBottom: b.level <= 2 ? 4 : 0,
            }}>{inlineParts(b.text)}</div>
          );
        }
        if (b.kind === 'p') return <p key={i} style={{ margin: '8px 0', textAlign: 'justify' }}>{inlineParts(b.text)}</p>;
        if (b.kind === 'ul') {
          return (
            <ul key={i} style={{ margin: '8px 0', paddingLeft: 22 }}>
              {b.items.map((it, j) => <li key={j} style={{ margin: '3px 0' }}>{inlineParts(it)}</li>)}
            </ul>
          );
        }
        if (b.kind === 'table') {
          const headRow = b.hasHeader ? b.rows[0] : null;
          const bodyRows = b.hasHeader ? b.rows.slice(1) : b.rows;
          return (
            <table key={i} style={{
              width: '100%', borderCollapse: 'collapse', fontSize: 12, margin: '12px 0',
              border: '1px solid #cbd5e1',
            }}>
              {headRow && (
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    {headRow.map((c, ci) => (
                      <th key={ci} style={{ padding: '6px 8px', borderRight: '1px solid #cbd5e1', textAlign: 'left', fontWeight: 700 }}>
                        {inlineParts(c)}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {bodyRows.map((row, ri) => (
                  <tr key={ri} style={{ borderTop: '1px solid #e2e8f0' }}>
                    {row.map((c, ci) => (
                      <td key={ci} style={{ padding: '5px 8px', borderRight: '1px solid #e2e8f0', verticalAlign: 'top' }}>
                        {inlineParts(c)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
        return null;
      })}
    </div>
  );
}

// ===========================================================================
// Print / Save-as-PDF — renders markdown to HTML in the parent, then writes
// the rendered HTML into a new window and triggers window.print().
// ===========================================================================
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function inlineToHtml(text: string): string {
  let out = escapeHtml(text);
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  return out;
}
function blockToHtml(b: Block): string {
  if (b.kind === 'hr') return '<hr style="border:0;border-top:1px solid #cbd5e1;margin:18px 0;"/>';
  if (b.kind === 'h') {
    const sizes: Record<number, string> = { 1: '22pt', 2: '15pt', 3: '12pt' };
    const border = b.level <= 2 ? 'border-bottom:1px solid #cbd5e1;padding-bottom:4px;' : '';
    return '<h' + b.level + ' style="font-size:' + sizes[b.level] + ';margin:18px 0 10px;' + border + '">' + inlineToHtml(b.text) + '</h' + b.level + '>';
  }
  if (b.kind === 'p') return '<p style="margin:8px 0;text-align:justify;">' + inlineToHtml(b.text) + '</p>';
  if (b.kind === 'ul') {
    return '<ul style="margin:8px 0;padding-left:22px;">' +
      b.items.map((it) => '<li style="margin:3px 0;">' + inlineToHtml(it) + '</li>').join('') +
      '</ul>';
  }
  if (b.kind === 'table') {
    const headRow = b.hasHeader ? b.rows[0] : null;
    const bodyRows = b.hasHeader ? b.rows.slice(1) : b.rows;
    const thead = headRow
      ? '<thead><tr style="background:#f1f5f9;">' +
        headRow.map((c) => '<th style="padding:6px 8px;border:1px solid #cbd5e1;text-align:left;font-weight:700;">' + inlineToHtml(c) + '</th>').join('') +
        '</tr></thead>'
      : '';
    const tbody = '<tbody>' +
      bodyRows.map((row) =>
        '<tr>' +
        row.map((c) => '<td style="padding:5px 8px;border:1px solid #e2e8f0;vertical-align:top;">' + inlineToHtml(c) + '</td>').join('') +
        '</tr>'
      ).join('') +
      '</tbody>';
    return '<table style="width:100%;border-collapse:collapse;font-size:11pt;margin:12px 0;border:1px solid #cbd5e1;">' + thead + tbody + '</table>';
  }
  return '';
}
function mdToHtml(text: string): string {
  return parseBlocks(text).map(blockToHtml).join('\n');
}
function printDraft(text: string, title: string): void {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) { alert('Pop-up blocked — allow pop-ups to print.'); return; }
  const safeTitle = escapeHtml(title);
  const html = mdToHtml(text);
  const doc = '<!doctype html><html><head><title>' + safeTitle + '</title>' +
    '<style>' +
    '  body { font-family: Georgia, "Times New Roman", serif; max-width: 8.5in; margin: 0.6in auto; color: #0f172a; line-height: 1.55; padding: 0 16px; }' +
    '  table { page-break-inside: auto; } tr { page-break-inside: avoid; }' +
    '  h1, h2, h3 { page-break-after: avoid; }' +
    '  @media print { body { margin: 0.4in; } }' +
    '</style></head><body>' +
    html +
    '<script>setTimeout(function(){window.print();},250);</script>' +
    '</body></html>';
  w.document.write(doc);
  w.document.close();
}

// ===========================================================================
// SourceDataView
// ===========================================================================
function SourceDataView({ source }: { source: any }) {
  const payload = source?.payload ?? {};
  const citations = source?.citations ?? [];
  const missing = source?.missing ?? [];
  return (
    <div style={{ fontSize: 11 }}>
      <div style={{ marginBottom: 10, padding: '6px 10px', background: '#f1f5f9', borderRadius: 3, fontFamily: 'ui-monospace, monospace', fontSize: 10 }}>
        <strong>{source?.template_version}</strong> · fetched {source?.fetched_at && new Date(source.fetched_at).toLocaleString()}
      </div>
      {missing.length > 0 && (
        <div style={{ marginBottom: 10, padding: '8px 10px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 3 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
            ⚠ Missing evidence ({missing.length})
          </div>
          {missing.map((m: any, i: number) => (
            <div key={i} style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
              <strong>{m.field}:</strong> {m.reason}
            </div>
          ))}
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
          Payload
        </div>
        <pre style={{
          margin: 0, padding: 8, background: '#0f172a', color: '#e2e8f0',
          fontSize: 10, borderRadius: 3, overflow: 'auto', maxHeight: 380,
          fontFamily: 'ui-monospace, monospace',
        }}>
          {JSON.stringify(payload, null, 2)}
        </pre>
      </div>
      {citations.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>
            Citations ({citations.length})
          </div>
          <div style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', color: '#475569' }}>
            {citations.map((c: any, i: number) => (
              <div key={i} style={{ padding: '2px 0', borderBottom: i < citations.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                <span style={{ color: '#7c3aed' }}>{c.table}</span>
                <span style={{ color: '#94a3b8' }}>/</span>
                <span>{String(c.id).slice(0, 36)}</span>
                {c.note && <span style={{ color: '#94a3b8', marginLeft: 6 }}>— {c.note}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
